import React from "react";

import cx from "classnames";

import { Tabs } from "~/ui/legacyFellowIndex";

import styles from "~/ui/Tabs/css/Tabs.module.css";

type Label = string | React.ReactElement | ((active: boolean) => React.ReactElement);

interface TabProps<T extends string> {
	label: Label;
	tabId: T;
	active: boolean;
	onClick: (e: React.MouseEvent<HTMLAnchorElement>, name: T) => void;
}

export function Tab<T extends string>({ label, tabId, active, onClick }: TabProps<T>) {
	// eslint-disable-next-line no-underscore-dangle
	const _onClick = React.useCallback((e: React.MouseEvent<HTMLAnchorElement>) => onClick(e, tabId), [onClick, tabId]);

	const labelHandlesInteraction = typeof label === "function";

	return (
		<a
			href={`#${tabId}`}
			className={cx(styles.tab, {
				[styles.tabActive]: active && !labelHandlesInteraction,
				[styles.tabHover]: !labelHandlesInteraction,
			})}
			onClick={_onClick}
		>
			{typeof label === "function" ? label(active) : label}
		</a>
	);
}

interface AlmostSimpleTabsProps<TabName extends string, Tabs extends readonly { key: TabName; label: Label }[]> {
	tab: TabName;
	tabs: Tabs;
	setTab: (name: TabName) => void;
	className?: string;
	small?: boolean;
}

export default function AlmostSimpleTabs<TabKey extends string, Tabs extends readonly { key: TabKey; label: Label }[]>({
	tab,
	tabs,
	setTab,
	className,
	small,
}: AlmostSimpleTabsProps<TabKey, Tabs>) {
	const onClick = React.useCallback(
		(e: React.MouseEvent<HTMLAnchorElement>, tab: TabKey) => {
			e.preventDefault();
			setTab(tab);
		},
		[setTab],
	);

	return (
		<Tabs className={className} small={small}>
			{tabs.map(tabDefinition => (
				<Tab
					key={tabDefinition.key}
					tabId={tabDefinition.key}
					label={tabDefinition.label}
					active={tab === tabDefinition.key}
					onClick={onClick}
				/>
			))}
		</Tabs>
	);
}
